import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from './auth.service';

const AUTH_RETRY_HEADER = 'X-Los-Auth-Retry';

function isAuthEndpoint(url: string): boolean {
  return url.includes('/staff-auth/login') || url.includes('/staff-auth/refresh');
}

/** Public UAT catalog — must work without staff login (see MockKycProfilesController). */
function isPublicMockKycCatalogEndpoint(url: string): boolean {
  return url.includes('/mock-kyc-profiles/');
}

/** Public OTP endpoints — do not run staff refresh/logout on 401. */
function isCustomerIdentityEndpoint(url: string): boolean {
  return url.includes('/customer-identity/');
}

/** Third-party geocoding / maps hosts must not receive staff JWT headers (CORS preflight). */
function isExternalGeocodingOrMapsHost(url: string): boolean {
  return (
    url.includes('maps.googleapis.com') ||
    url.includes('nominatim.openstreetmap.org')
  );
}

/** LOS's StaffJwtGuard reads X-Acting-User-Token specifically; Authorization is set too for tooling/Swagger parity. */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  if (isAuthEndpoint(req.url) || isExternalGeocodingOrMapsHost(req.url)) {
    return next(req);
  }

  const token = authService.getAccessToken();
  const authReq = token
    ? req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
          'X-Acting-User-Token': token,
        },
      })
    : req;

  return next(authReq).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
        return throwError(() => error);
      }

      if (isCustomerIdentityEndpoint(req.url)) {
        return throwError(() => error);
      }

      // Already retried after refresh — force logout.
      if (req.headers.has(AUTH_RETRY_HEADER)) {
        authService.logout();
        return throwError(() => error);
      }

      if (!authService.getRefreshToken()) {
        if (!isPublicMockKycCatalogEndpoint(req.url)) {
          authService.logout();
        }
        return throwError(() => error);
      }

      return authService.refreshSession().pipe(
        switchMap(() => {
          const refreshed = authService.getAccessToken();
          if (!refreshed) {
            authService.logout();
            return throwError(() => error);
          }
          return next(
            req.clone({
              setHeaders: {
                Authorization: `Bearer ${refreshed}`,
                'X-Acting-User-Token': refreshed,
                [AUTH_RETRY_HEADER]: '1',
              },
            }),
          );
        }),
        catchError((refreshError) => {
          if (!isPublicMockKycCatalogEndpoint(req.url)) {
            authService.logout();
          }
          return throwError(() => refreshError);
        }),
      );
    }),
  );
};
